<template>
  <admin-wrapper
    :straight-title="additionalModuleName"
    :inputs="inputs"
    :moduleEnabler="moduleEnabler"
    :is-form-fetched="isFormFetched"
    save-button-translation-path="button.sync"
    :is-custom-saving="syncing"
    @save="onSave"
  >
    <template #ilsEasy.input>
      <grey-tabs v-model="tab" :tabs="tabItems" cols="auto" has-border>
        <v-tab-item v-for="(item, idx) in tabItems" :key="idx">
          <component :is="item.component" :syncing="syncing" class="mt-8" :configuration.sync="configuration" />
        </v-tab-item>
      </grey-tabs>
    </template>
    <template #ils_easy_logo_1.input="{dropzoneOptions, events, name }">
      <file-input-image
        ref="ils_easy_logo_1"
        class="pl-3"
        :options="dropzoneOptions"
        :disabled="syncing"
        :events="events"
        type="customLogo"
        :name="name"
        :max-height="200"
        :max-width="200"
      />
    </template>
    <template #ils_easy_logo_2.input="{dropzoneOptions, events, name }">
      <file-input-image
        ref="ils_easy_logo_2"
        class="pl-3"
        :options="dropzoneOptions"
        :disabled="syncing"
        :events="events"
        type="customLogo"
        :name="name"
        :max-height="200"
        :max-width="200"
      />
    </template>
  </admin-wrapper>
</template>
<script>
import { call, get } from 'vuex-pathify';
import AdminWrapper from '@/components/AdminWrapper';
import FileInputImage from '@/components/FileInputImage';
import GreyTabs from '@/components/GreyTabs';
import IlsEasySettingsData from '@/components/IlsEasySettingsData';
import IlsEasySettingsPermissions from '@/components/IlsEasySettingsPermissions';

export default {
  name: 'IlsEasySettings',
  components: { AdminWrapper, FileInputImage, GreyTabs, IlsEasySettingsData, IlsEasySettingsPermissions },
  computed: {
    ilsEasyConfiguration: get('admin/settingsValues@ils_easy_configuration'),
    additionalModuleName: get('admin/additionalModules@ILS_EASY_MODULE.verbose_name'),
    isModuleEnabled: get('admin/settingsValues@ils_easy_module_enabled'),
    token: get('authentication/token'),
    moduleEnabler() {
      return {
        name: 'ils_easy_module_enabled',
        straightText: this.additionalModuleName,
        subtitle: this.$i18n.t('admin.moduleEnablerSubtitle', { suffix: this.additionalModuleName }),
      };
    },
    inputs() {
      return [
        {
          name: 'ils_easy_logo_1',
          translationPath: 'admin.ilsEasy.logo1',
          subtitle: 'admin.ilsEasy.logo1Subtitle',
          dropzoneOptions: {
            url: `${process.env.VUE_APP_API_URL}/settings/logo/ils_easy_logo_1`,
            method: 'put',
            urlFile: this.images.ils_easy_logo_1.url,
            urlGet: this.images.ils_easy_logo_1.url,
            acceptedFiles: '.png',
            maxFiles: 1,
            autoProcessQueue: true,
            paramName: 'logo',
            accept: (file, done) => {
              const self = this;
              const img = new Image();
              img.src = URL.createObjectURL(file);
              img.onload = function() {
                if (this.height > self.maxHeight || this.width > self.maxWidth) {
                  done('wrongResolution');
                } else {
                  done();
                }
              };
            },
          },
          events: {
            'vdropzone-success': () => {
              this.refreshLogoUrl('ils_easy_logo_1');
              this.$refs['ils_easy_logo_1']?.$refs['file']?.$refs['dropzone']?.removeAllFiles();
            },
          },
        },
        {
          name: 'ils_easy_logo_2',
          translationPath: 'admin.ilsEasy.logo2',
          subtitle: 'admin.ilsEasy.logo2Subtitle',
          dropzoneOptions: {
            url: `${process.env.VUE_APP_API_URL}/settings/logo/ils_easy_logo_2`,
            method: 'put',
            urlFile: this.images.ils_easy_logo_2.url,
            urlGet: this.images.ils_easy_logo_2.url,
            acceptedFiles: '.png',
            maxFiles: 1,
            autoProcessQueue: true,
            paramName: 'logo',
            accept: (file, done) => {
              const self = this;
              const img = new Image();
              img.src = URL.createObjectURL(file);
              img.onload = function() {
                if (this.height > self.maxHeight || this.width > self.maxWidth) {
                  done('wrongResolution');
                } else {
                  done();
                }
              };
            },
          },
          events: {
            'vdropzone-success': () => {
              this.refreshLogoUrl('ils_easy_logo_2');
              this.$refs['ils_easy_logo_2']?.$refs['file']?.$refs['dropzone']?.removeAllFiles();
            },
          },
        },
        {
          name: 'ilsEasy',
          isNamedSlot: false,
        },
      ];
    },
    tabItems() {
      return [
        {
          name: this.$i18n.t('admin.ilsEasy.data'),
          component: 'IlsEasySettingsData',
        },
        {
          name: this.$i18n.t('admin.ilsEasy.permissions.permissions'),
          component: 'IlsEasySettingsPermissions',
        },
      ];
    },

    syncing() {
      return this.loading || Boolean(this.currentTaskId);
    },
  },
  data: () => ({
    currentTaskId: null,
    isFormFetched: false,
    loading: false,
    tab: 0,

    configuration: {
      street_lamp_id: null,
      street_attribute: '',
      district_attribute: '',
      basemap_daylight_id: null,
      basemap_nightlight_id: null,
      basemap_satellite_id: null,
      predefined_maps: [],
      symbolization_layers_1: {
        name: '',
        current: null,
        masterplan: null,
      },
      symbolization_layers_2: {
        name: '',
        current: null,
        masterplan: null,
      },
      symbolization_layers_3: {
        name: '',
        current: null,
        masterplan: null,
      },
    },
    images: {
      ils_easy_logo_1: {
        file: undefined,
        src: '',
        url: '',
      },
      ils_easy_logo_2: {
        file: undefined,
        src: '',
        url: '',
      },
    },
  }),
  methods: {
    getLayers: call('layers/getLayers'),
    getDataSources: call('layers/getLayersMetadata'),
    getIlsLayers: call('ils_easy/getIlsLayers'),
    getUsersGroups: call('users/getGroups'),
    getProjects: call('layers/getProjects'),
    ilsEasySync: call('ils_easy/ilsEasySync'),
    async init() {
      this.isFormFetched = false;
      await Promise.all([
        this.getDataSources({ with_attributes_schema: true }),
        this.getLayers(),
        this.getIlsLayers(),
        this.getUsersGroups(),
        this.getProjects(),
      ]);
      this.configuration = { ...this.configuration, ...this.ilsEasyConfiguration };
      this.refreshLogoUrl('ils_easy_logo_1');
      this.refreshLogoUrl('ils_easy_logo_2');
      this.isFormFetched = true;
    },
    async onSave() {
      this.loading = true;
      try {
        this.currentTaskId = await this.ilsEasySync();
        this.$globalHub.$emit('handleCeleryTask', {
          skipTaskResult: true,
          currentTaskId: this.currentTaskId,
          taskStartSnackbar: {
            message: this.$i18n.t('snackbar.ilsEasy'),
          },
          taskFailureSnackbar: {
            message: this.$i18n.t('snackbar.ilsEasyError'),
          },
          taskFailureCallback: this.endSave,
          taskSuccessCallback: () => {
            this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
              message: this.$i18n.t('snackbar.ilsEasyCompleted'),
            });
            this.endSave();
          },
        });
      } catch {
        this.endSave();
      } finally {
        this.loading = false;
      }
    },
    endSave() {
      this.loading = false;
      this.currentTaskId = null;
    },

    refreshLogoUrl(name) {
      this.images[name].url = `${process.env.VUE_APP_API_URL}/settings/logo/${name}?token=${
        this.token
      }&cache=${Date.now()}`;
    },
  },
  watch: {
    isModuleEnabled(nV) {
      if (nV) {
        this.init();
      }
    },
  },
  created() {
    if (this.isModuleEnabled) {
      this.init();
    } else {
      this.isFormFetched = true;
    }
  },
};
</script>
